/**
 * Create a promoted-only post: text, an image (uploaded or from the media library) and an
 * optional card (website, or an X lead form).
 *
 * A post carries EITHER images OR a card (the card holds the image). The parts are created in
 * order — image → card → post — and each one that succeeded is kept, so "Try again" after a
 * failure only redoes what is missing.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ImagePlus, Loader2, RefreshCw, Send, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { XSelect } from './XSelect';
import { errMsg, xAdsService, type XLeadForm, type XMedia, type XPost } from '@/services/x-ads.service';

type CardKind = 'none' | 'website' | 'lead';
type Part = 'image' | 'card' | 'post';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function PostComposer({ uid, onCreated }: { uid: string; onCreated: (post: XPost) => void }) {
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [libraryKey, setLibraryKey] = useState('');
  const [library, setLibrary] = useState<XMedia[] | null>(null);
  const [cardKind, setCardKind] = useState<CardKind>('none');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('https://');
  const [leadFormId, setLeadFormId] = useState('');
  const [leadForms, setLeadForms] = useState<XLeadForm[] | null>(null);
  // What already exists on X for this draft.
  const [uploaded, setUploaded] = useState<{ file: File; mediaKey: string } | null>(null);
  const [card, setCard] = useState<{ sig: string; cardUri: string } | null>(null);
  const [busy, setBusy] = useState<Part | null>(null);
  const [failed, setFailed] = useState<{ part: Part; message: string } | null>(null);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  useEffect(() => {
    if (cardKind !== 'lead' || leadForms) return;
    xAdsService.leadForms(uid).then(setLeadForms).catch(() => setLeadForms([]));
  }, [cardKind, leadForms, uid]);

  const mediaKey = (uploaded && uploaded.file === file ? uploaded.mediaKey : '') || (!file ? libraryKey : '');
  const hasImage = !!file || !!libraryKey;
  const cardSig = JSON.stringify([cardKind, title.trim(), url.trim(), leadFormId, name.trim()]);

  const problem = (): string | null => {
    if (text.length > 280) return 'Post text must be 280 characters or fewer.';
    if (!text.trim() && !hasImage) return 'Write the post text or add an image.';
    if (file && (!IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES)) return 'Images must be JPEG, PNG, GIF or WEBP, 5 MB or smaller.';
    if (cardKind !== 'none') {
      if (!hasImage) return 'A card needs an image.';
      if (!title.trim()) return 'Give the card a headline.';
      if (title.trim().length > 70) return 'The card headline must be 70 characters or fewer.';
      if (cardKind === 'website' && !/^https:\/\/[^\s/$.?#].[^\s]*$/i.test(url.trim())) return 'The website must be a full https:// address.';
      if (cardKind === 'lead' && !leadFormId) return 'Pick the lead form the card opens.';
    }
    return null;
  };

  const pickFile = (f: File | null) => { setFile(f); if (f) setLibraryKey(''); setFailed(null); };

  const submit = async () => {
    const p = problem();
    if (p) { toast.error(p); return; }
    setFailed(null);
    let key = mediaKey;
    let part: Part = 'image';
    try {
      if (file && !key) {
        setBusy('image');
        key = (await xAdsService.uploadImage(uid, file)).mediaKey;
        setUploaded({ file, mediaKey: key });
      }
      let cardUri = '';
      if (cardKind !== 'none') {
        part = 'card';
        if (card?.sig === cardSig) cardUri = card.cardUri;
        else {
          setBusy('card');
          const created = await xAdsService.createCard(uid, {
            name: (name.trim() || title.trim()).slice(0, 80), mediaKey: key, title: title.trim(),
            ...(cardKind === 'website' ? { websiteUrl: url.trim() } : { leadFormId }),
          });
          if (!created.cardUri) throw new Error('X did not return the card.');
          cardUri = created.cardUri;
          setCard({ sig: cardSig, cardUri });
        }
      }
      part = 'post';
      setBusy('post');
      const post = await xAdsService.createPost(uid, { text: text.trim() || undefined, name: name.trim() || undefined, ...(cardUri ? { cardUri } : key ? { mediaKeys: [key] } : {}) });
      toast.success('Post created on X');
      onCreated(post);
      setText(''); setName(''); setFile(null); setLibraryKey(''); setCardKind('none'); setTitle(''); setUrl('https://'); setLeadFormId('');
      setUploaded(null); setCard(null);
    } catch (e) {
      setFailed({ part, message: errMsg(e) });
    } finally {
      setBusy(null);
    }
  };

  const steps: Array<{ part: Part; label: string; done: boolean; show: boolean }> = [
    { part: 'image', label: 'Image uploaded', done: !!uploaded && uploaded.file === file, show: !!file },
    { part: 'card', label: 'Card created', done: card?.sig === cardSig, show: cardKind !== 'none' },
  ];

  return <div className="grid gap-x-6 lg:grid-cols-[minmax(0,1fr)_260px]">
    <div>
      <FormField label="Post text" htmlFor="xp-text" hint={`${text.length}/280`}>
        <Textarea id="xp-text" rows={4} maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder="What should people see in their timeline?" />
      </FormField>
      <FormField label="Internal name" htmlFor="xp-name" hint="Optional, only you see it">
        <Input id="xp-name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>
      <FormField label="Image" hint="JPEG, PNG, GIF or WEBP · 5 MB max">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-surface-alt">
            <ImagePlus size={15} />{file ? 'Replace image' : 'Upload image'}
            <input type="file" accept={IMAGE_TYPES.join(',')} className="sr-only" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
          </label>
          {!library && <Button variant="ghost" size="sm" onClick={() => xAdsService.media(uid).then(setLibrary).catch(() => setLibrary([]))}>Use one from the media library</Button>}
          {(file || libraryKey) && <Button variant="ghost" size="sm" onClick={() => { pickFile(null); setLibraryKey(''); }}>Remove image</Button>}
        </div>
        {library && <div className="mt-2 grid max-h-48 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
          {library.length === 0 && <p className="xa-muted col-span-full">The media library is empty.</p>}
          {library.filter((m) => m.mediaKey).map((m) => <button key={m.mediaKey} type="button" title={m.name || m.fileName || m.mediaKey}
            aria-pressed={libraryKey === m.mediaKey} onClick={() => { setLibraryKey(m.mediaKey!); setFile(null); }}
            className={`aspect-square overflow-hidden rounded-md border-2 ${libraryKey === m.mediaKey ? 'border-primary' : 'border-transparent'}`}>
            {m.mediaUrl ? <img src={m.mediaUrl} alt={m.name || ''} className="h-full w-full object-cover" loading="lazy" /> : <span className="text-[10px]">{m.name || m.mediaKey}</span>}
          </button>)}
        </div>}
      </FormField>
      <FormField label="Card" htmlFor="xp-card" hint="A card turns the image into a button">
        <XSelect id="xp-card" value={cardKind} onChange={(v) => v && setCardKind(v as CardKind)} options={[
          { value: 'none', label: 'No card — image or text only' }, { value: 'website', label: 'Website card' }, { value: 'lead', label: 'Lead form card' },
        ]} />
      </FormField>
      {cardKind !== 'none' && <div className="grid gap-x-4 sm:grid-cols-2">
        <FormField label="Card headline" htmlFor="xp-title" required hint={`${title.length}/70`}>
          <Input id="xp-title" maxLength={70} value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormField>
        {cardKind === 'website'
          ? <FormField label="Website" htmlFor="xp-url" required><Input id="xp-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" /></FormField>
          : <FormField label="Lead form" htmlFor="xp-form" required hint={leadForms?.length === 0 ? 'Create one under Tools' : undefined}>
              <XSelect id="xp-form" value={leadFormId} onChange={setLeadFormId} disabled={!leadForms?.length}
                placeholder={leadForms ? 'Choose a lead form' : 'Loading…'} options={(leadForms ?? []).map((f) => ({ value: f.id, label: f.name || f.id }))} />
            </FormField>}
      </div>}
      {failed && <p role="alert" className="mb-3 flex items-start gap-1.5 text-sm text-danger"><XCircle size={15} className="mt-0.5 shrink-0" />{failed.part === 'image' ? 'Image upload' : failed.part === 'card' ? 'Card' : 'Post'} failed: {failed.message}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={submit} loading={!!busy} iconLeft={failed ? <RefreshCw size={14} /> : <Send size={14} />}>{failed ? 'Try again' : 'Create post'}</Button>
        {busy && <span className="xa-muted">{busy === 'image' ? 'Uploading the image to X…' : busy === 'card' ? 'Creating the card…' : 'Creating the post…'}</span>}
        {steps.filter((s) => s.show && s.done).map((s) => <span key={s.part} className="flex items-center gap-1 text-xs text-success"><CheckCircle2 size={13} />{s.label}</span>)}
      </div>
    </div>
    <aside aria-label="Preview" className="mt-4 lg:mt-0">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Preview</p>
      <div className="rounded-xl border border-border bg-surface p-3 text-sm">
        <p className="whitespace-pre-wrap break-words">{text || <span className="text-text-muted">Your post text</span>}</p>
        {(preview || libraryKey) && <div className="mt-2 overflow-hidden rounded-lg border border-border">
          {preview ? <img src={preview} alt="" className="max-h-48 w-full object-cover" /> : <img src={library?.find((m) => m.mediaKey === libraryKey)?.mediaUrl} alt="" className="max-h-48 w-full object-cover" />}
          {cardKind !== 'none' && <p className="border-t border-border px-3 py-2 text-xs font-medium">{title || 'Card headline'}<span className="block font-normal text-text-muted">{cardKind === 'website' ? url.replace(/^https?:\/\//, '') : 'Opens a lead form'}</span></p>}
        </div>}
        {busy && <Loader2 size={14} className="mt-2 animate-spin text-text-muted" />}
      </div>
      <p className="xa-muted mt-2">Posts made here are promoted-only: they never appear on your profile's timeline.</p>
    </aside>
  </div>;
}
